import React, { useState, useEffect, useCallback, useContext } from 'react';
import PropTypes from 'prop-types';
import CheckboxTree from 'react-checkbox-tree';
import { Input, Spinner } from '@agconnections/grow-ui';
import {
  Checkbox,
  FormGroup,
  FormControlLabel,
  createTheme,
  ThemeProvider
} from '@material-ui/core';
import { obtainAllKeys } from 'screens/Reports/helpers/helperFunctions';
import 'react-checkbox-tree/lib/react-checkbox-tree.css';
import { intersection } from 'lodash';
import {
  ArrowRight,
  ArrowDropDown,
  CheckBox,
  CheckBoxOutlineBlank,
  IndeterminateCheckBox
} from '@material-ui/icons';
import { useFormikContext } from 'formik';
import { ReportsContext } from 'screens/Reports/context/ReportsProvider';

const theme = createTheme({
  palette: {
    primary: {
      main: '#0071cd'
    }
  }
});

const productTypeDisplay = key => {
  switch (key) {
    case 'SEED':
      return 'Seed';
    case 'CROPPROTECTION':
      return 'Crop Protection';
    case 'FERTILIZER':
      return 'Fertilizer';
    case 'SERVICE':
      return 'Service';
    default:
      return 'Custom Product';
  }
};

const groupByProductsDefaultPropMappedData = [];
const GroupByProducts = ({
  mappedData = groupByProductsDefaultPropMappedData,
  disabled = false,
  loading = false
}) => {
  const { setFieldValue } = useFormikContext();
  const {
    state: { formSelections }
  } = useContext(ReportsContext);

  const [checked, setChecked] = useState([]);
  const [expanded, setExpanded] = useState([]);
  const [filterText, setFilterText] = useState();
  const [productsNode, setProductsNode] = useState([]);
  const [nodesFiltered, setNodesFiltered] = useState(productsNode);

  useEffect(() => {
    let mappedFft = [];
    if (mappedData) {
      mappedFft = mappedData?.map((eachProduct, parentIndex) => ({
        value: `CP_${parentIndex}`,
        label: productTypeDisplay(eachProduct.productType),
        type: 'type',
        ...(eachProduct.children.length > 0
          ? {
              children: eachProduct.children?.map(item => {
                return {
                  id: item.productId,
                  value: `PN_${item.productId}`,
                  label: item.productName,
                  type: 'name'
                };
              })
            }
          : { disabled: true })
      }));
    }
    setProductsNode(mappedFft);
  }, [mappedData]);

  const handleCheck = checkedIds => {
    setChecked(checkedIds);
  };
  useEffect(() => {
    // A product type with no children is mapped without a `children` key, so
    // flattening leaves a hole that the lookup below would dereference.
    const products = productsNode.flatMap(({ children }) => children ?? []);

    const productMatches = checked
      .filter(id => id.startsWith('PN_'))
      .map(_id => products.find(product => product.value === _id))
      .filter(product => !!product)
      .map(({ id }) => id);

    setFieldValue('checkedProducts', checked);
    setFieldValue('selectedProducts', [...new Set(productMatches)]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checked]);

  useEffect(() => {
    handleCheck();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productsNode]);

  const filterNodes = useCallback(
    (filtered, node) => {
      const children = (node.children || []).reduce(
        (acc, current) => filterNodes(acc, current),
        []
      );

      if (
        node.label
          .toLocaleLowerCase()
          .indexOf(filterText?.toLocaleLowerCase()) > -1 ||
        children.length
      ) {
        filtered.push({ ...node, children });
      }

      return filtered;
    },
    [filterText]
  );

  const filterTree = () => {
    if (!filterText?.length) {
      setNodesFiltered(productsNode);
      return;
    }

    setNodesFiltered(nodes =>
      nodes.reduce((acc, current) => filterNodes(acc, current), [])
    );
  };

  const onFilterChange = e => {
    if (e.target.value.length >= 3) {
      setFilterText(e.target.value);
      filterTree();
    } else {
      setFilterText('');
    }
  };

  useEffect(() => {
    if (!filterText) {
      setNodesFiltered(productsNode);
      return;
    }

    setNodesFiltered(prevState =>
      prevState.reduce((acc, current) => filterNodes(acc, current), [])
    );
  }, [filterText, filterNodes, productsNode]);

  useEffect(() => {
    const allKeys = obtainAllKeys(productsNode);
    if (intersection(formSelections.products, allKeys).length > 0) {
      setChecked(formSelections.products);
    } else {
      setChecked(obtainAllKeys(productsNode));
    }
  }, [productsNode, formSelections.products]);

  const productKeys = obtainAllKeys(productsNode);

  const handleSelectAllCheckBox = () => {
    if (checked.length === productKeys.length) {
      setChecked([]);
    } else {
      setChecked(productKeys);
    }
  };
  return (
    <div className="filter-container h-full">
      {loading ? (
        <Spinner size="sm" />
      ) : (
        <>
          {mappedData?.length > 0 && (
            <>
              <div
                className="my-2"
                data-testid="product-filter-container"
                style={{ display: 'none' }}
              >
                <Input
                  className="filter-text"
                  placeholder="Search..."
                  type="search"
                  onChange={onFilterChange}
                />
              </div>
              <div className="mt-2 flex">
                <FormGroup>
                  <FormControlLabel
                    control={
                      <ThemeProvider theme={theme}>
                        <Checkbox
                          style={{ borderColor: '#00000099' }}
                          size="small"
                          color="primary"
                          onChange={handleSelectAllCheckBox}
                          checked={checked?.length === productKeys?.length}
                        />
                      </ThemeProvider>
                    }
                    label="Select All"
                  />
                </FormGroup>
              </div>

              <CheckboxTree
                nodes={nodesFiltered}
                checked={checked}
                showNodeIcon={false}
                icons={{
                  halfCheck: <IndeterminateCheckBox fontSize="small" />,
                  uncheck: (
                    <CheckBoxOutlineBlank
                      fontSize="small"
                      className="text-neutral-600"
                    />
                  ),
                  check: <CheckBox fontSize="small" />,
                  expandClose: (
                    <ArrowRight fontSize="small" className="text-neutral-600" />
                  ),
                  expandOpen: (
                    <ArrowDropDown
                      fontSize="small"
                      className="text-neutral-600"
                    />
                  )
                }}
                css="text-lg"
                expanded={expanded}
                onCheck={id => handleCheck(id)}
                onExpand={ex => setExpanded(ex)}
                showNodeTitle
                disabled={disabled}
                checkModel="all"
              />
            </>
          )}
        </>
      )}
    </div>
  );
};

GroupByProducts.propTypes = {
  mappedData: PropTypes.arrayOf(PropTypes.object),
  disabled: PropTypes.bool,
  loading: PropTypes.bool
};

export default GroupByProducts;
